import { clientFetch } from '~/lib/.client/fetch';
import type {
  AddDomainActionBody,
  AddDomainActionResponse,
  PurchaseDomainActionBody,
  PurchaseDomainActionResponse,
  AddDomainAliasActionResponse,
  SearchDomainActionResponse,
} from '~/routes/api.domains.$action.($id)';

export async function verifyDomain(domain: string) {
  const dnsResponse = await checkDnsStatus(domain);

  if (dnsResponse.Status === 3) {
    return 'non-existent';
  }

  if (dnsResponse.Status === 0) {
    return 'valid';
  }

  return 'unknown';
}

interface DnsResponse {
  /**
   * @see https://www.iana.org/assignments/dns-parameters/dns-parameters.xhtml#dns-parameters-6
   *
   * Relevant status codes:
   *
   * ```
   * 0: No error
   * 1: Format error
   * 2: Server failure
   * 3: Non-Existent Domain
   * ```
   */
  Status: number;

  Answer?: {
    name: string;
    type: number;
    TTL: number;
    data: string;
  }[];
}

async function checkDnsStatus(domain: string) {
  const url = new URL('https://cloudflare-dns.com/dns-query');

  url.searchParams.set('name', domain);

  const response = await clientFetch(url, {
    headers: {
      accept: 'application/dns-json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to check DNS status.');
  }

  return await response.json<DnsResponse>();
}

export async function addDomainToProject(domain: string, projectId: string) {
  const response = await clientFetch('/api/domains/add', {
    method: 'POST',
    body: JSON.stringify({ domain, projectId } satisfies AddDomainActionBody),
  });

  const result = await response.json<AddDomainActionResponse>();

  if (result && result.success) {
    return { siteUrl: result.site_url, entries: result.entries };
  }

  throw createError(result, response.status);
}

/**
 * Purchase a domain for a project and an optional organization.
 *
 * @param domain The domain to purchase.
 * @param projectId The project ID to purchase the domain for.
 * @param organization Optional organization information to purchase the domain for.
 */
export async function purchaseDomainForProject(
  projectId: string,
  organization: { id: number } | null | undefined,
  domain: { force: boolean; domain: string; firstYearPrice: number; price: number },
) {
  const data: PurchaseDomainActionBody = { ...domain };

  // if an organization is provided, it means we are purchasing the domain for an organization
  if (organization) {
    data.organization = organization.id;
  }

  const response = await clientFetch(`/api/domains/purchase/${projectId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  const result = await response.json<PurchaseDomainActionResponse>();

  if (result) {
    if (result.success) {
      return { status: 'success' as const, siteUrl: result.site_url, entries: result.entries };
    }

    if (result.code === 'payment_required') {
      return { status: 'payment_required' as const, paymentUrl: result.paymentUrl };
    }
  }

  throw createError(result, result?.code ?? response.status);
}

export async function searchDomain(keyword: string) {
  const response = await clientFetch(`/api/domains/search?search=${encodeURIComponent(keyword)}`);

  if (!response.ok) {
    throw new Error('Failed to fetch domain search results');
  }

  return await response.json<SearchDomainActionResponse>();
}

function createError(result: { success: false; error: string } | undefined, code: number | string) {
  const error = new Error(result?.error || 'An unknown error occurred.');

  (error as any).code = code;

  return error;
}

export async function removeNetlifyHosting(projectId: string) {
  try {
    const response = await clientFetch(`/api/deploy/${projectId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to remove Netlify hosting');
    }
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to remove Netlify hosting');
  }
}

export async function addDomainAlias(projectId: string, alias: string) {
  try {
    const response = await clientFetch(`/api/domains/domain_alias/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify({ alias }),
    });

    const data = await response.json<AddDomainAliasActionResponse>();

    if (!data.success) {
      throw new Error(data.error);
    }

    if (!response.ok) {
      throw new Error('Failed to add domain alias');
    }

    return data.entries;
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to add domain alias');
  }
}
